import { uniq } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import {
  combineLatest,
  distinctUntilChanged,
  map,
  merge,
  Observable,
  startWith,
  Subject,
  Subscription,
  switchMap,
  takeWhile,
} from 'rxjs'
import { AlertFactory } from 'alerts/Alert'
import type {
  AdminDocumentsQuery,
  DocumentsSortField,
  SearchDocumentsQuery,
} from 'documents/DocumentStore'
import Document from 'documents/Document'
import DocumentStore from 'documents/DocumentStore'
import { InfiniteSelectFilter } from 'infra/FilterControls/InfiniteSelect/InfiniteSelectFilter'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import {
  FilterItemSource,
  FilterStorage,
  FiltersViewModel,
} from 'infra/FiltersViewModel'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { JobScopeOption } from 'jobs/dtos/job'
import JobStore from 'jobs/JobStore'
import { CursorPaged, CursorPageQuery, Direction, Id } from 'types/definitions'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { areDeepEqual } from 'utils/object-util'
import { shareLatest, withCancellation } from 'utils/rx-util'
import { fromStream, tapAction, toStream } from 'utils/mobx-util'
import { DocumentsLoadFilterItemsPorts } from './DocumentsLoadFilterItemsAdapter'

/**
 * Document search view model.
 */
export class DocumentSearchViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * Triggers a search immediately with the current filter.
   */
  private searchTrigger = new Subject()

  /**
   * Backing field for the last search.
   */
  @observable.ref private _lastSearch: DocumentSearchResult = {
    documents: [],
    searchFilter: DocumentSearchFilter.none,
    total: 0,
    filtersApplied: false,
  }

  /**
   * Total documents from the last search.
   */
  @observable
  totalDocuments = 0

  /**
   * Whether the filter controls are visible.
   */
  @observable
  visible = true

  /**
   * Sorting.
   */
  @observable
  sorting: DocumentSort = {
    field: undefined,
    direction: undefined,
  }

  /**
   * Search text.
   */
  searchText = new SearchTextViewModel()

  filters = new FiltersViewModel<DocumentFilter>({
    filterStorage: this.filterStorage,
    definitions: {
      workspaces: {
        title: 'Workspaces',
        implicit: true,
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static(this.getWorkspaces()),
            true,
          ),
      },
      jobs: {
        icon: 'jobs',
        title: 'Jobs',
        factory: (key, title) =>
          new InfiniteSelectFilter(
            key,
            title,
            async (paging, _, filter) =>
              this.fetchSearchableJobs(paging, filter),
            true,
          ),
      },
      documentTypes: {
        icon: 'documents',
        title: 'Document types',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            async () => this.fetchDocumentTypes(),
            true,
          ),
      },
      archived: {
        icon: 'delete',
        title: 'Archived',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              { value: true, title: 'Archived only' },
              { value: false, title: 'Unarchived only' },
            ]),
            false,
          ),
      },
    },
  })

  /**
   * Documents scroller.
   */
  scroller = new InfiniteScrollProvider<Document, DocumentSearchFilter>({
    limit: 20,
    // We trigger the initial fetch ourselves. If this was false,
    // we would see 2 initial fetches.
    startFetchTaskAsPending: true,
    fetchItems: (paging, _, filter) =>
      this.fetchDocuments(
        toSearchDocumentsQuery(this.userPublicId, paging, filter),
      ),
  })

  /**
   * Search filter changes.
   */
  private searchFilter$ = combineLatest([
    // Combine the search text changes
    this.searchText.debouncedValue$,
    // ...with the remaining filter changes
    this.filters.appliedFilters$,
    // ...and the sorting changes.
    toStream(() => this.sorting, true),
  ]).pipe(
    // Map what we've combined to a Search Filter.
    map(([searchText, appliedFilters, sort]): DocumentSearchFilter => {
      return {
        sort,
        searchText: searchText ?? '',
        workspaceIds: appliedFilters.workspaces,
        jobIds: appliedFilters.jobs,
        documentTypeIds: appliedFilters.documentTypes,
        archived: appliedFilters.archived?.slice().pop(),
      }
    }),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  /**
   * Searches observable. Each emitted value is the search result of
   * a completed query.
   */
  searches$ = merge(
    // When the search filter changes
    this.searchFilter$,
    // Or when we explicitly request a search for the current filter (can be used to skip debouncing)
    this.searchTrigger.pipe(
      // Since searchFilter$ is using replay, it will emit the last
      // search filter immediately on subscribe.
      switchMap(() => this.searchFilter$),
    ),
  ).pipe(
    // Start a non-filtered search immediately.
    startWith(DocumentSearchFilter.none),
    // Ignore emissions with the same search filter.
    distinctUntilChanged<DocumentSearchFilter>(DocumentSearchFilter.equals),
    // Start a search, return the filter that was used.
    switchMap(
      (query): Observable<DocumentSearchResult> =>
        withCancellation(async (ct) => {
          let result = await this.scroller.fetch(query)
          // Keep loading more until we get something back that isn't being filtered out
          // on the client. If we don't do this here, we potentially display an empty state
          // which does not trigger infinite scroll.
          while (this.documents.length === 0 && !this.scroller.isDone) {
            ct.throwIfCancelled()
            result = await this.scroller.more()
          }

          return {
            searchFilter: query,
            filtersApplied: !DocumentSearchFilter.isNone(query),
            total: result?.total || this.documents.length,
            documents: this.documents,
          }
        }),
    ),
    // Make sure to only subscribe once.
    shareLatest(),
  )

  constructor(
    private _userPublicId: () => Id,
    private loadFilterItemsAdapter: DocumentsLoadFilterItemsPorts,
    private documentStore: DocumentStore,
    private jobStore: JobStore,
    private filterStorage: FilterStorage,
    private newAlert: AlertFactory,
  ) {
    makeObservable(this)
  }

  /**
   * Activates the view model.
   */
  activate() {
    this.filters.activate()

    this.subscription =
      // Search requests.
      this.searches$
        .pipe(
          tapAction((search) => {
            this._lastSearch = search
            this.totalDocuments = search.total
          }),
        )
        .subscribe()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.filters.deactivate()
    this.subscription.unsubscribe()
  }

  /**
   * The internal documents list ran through the transformer.
   * Honors the applied archived filter so documents archived after
   * being fetched drop out of the list reactively.
   */
  @computed
  get documents() {
    const documents = uniq(this.scroller.items)
    const archived = this.lastSearch.searchFilter.archived

    // When the archived filter isn't being used, return all documents
    if (archived === undefined) {
      return documents
    }

    // Otherwise, filter out documents not matching the archived filter
    // that's include the documents archived when the filter is implemented
    return documents.filter((doc) => doc.archived === archived)
  }

  /**
   * The currently selected user.
   */
  @computed
  get userPublicId() {
    return this._userPublicId()
  }

  /**
   * Last search computed getter.
   */
  @computed
  get lastSearch() {
    return this._lastSearch
  }

  /**
   * Gets the current search state.
   */
  @computed
  get searchState(): SearchState {
    if (this.getIsFirstFetchRunning()) {
      return SearchState.InitialSearchRunning
    }

    if (this.lastSearch.documents.length === 0 && this.scroller.isDone) {
      return this.lastSearch.filtersApplied
        ? SearchState.EmptyButFilterApplied
        : SearchState.CompletelyEmpty
    }

    return SearchState.Some
  }

  /**
   * Gets the workspaces filter.
   */
  @computed
  get workspacesFilter() {
    return this.filters.getFilter('workspaces') as SelectFilter<string, Id>
  }

  /**
   * Sets the document sort field.
   */
  @action.bound
  setSortField(field?: DocumentsSortField) {
    this.sorting = {
      field,
      direction: this.sorting.direction,
    }
  }

  /**
   * Sets the document sort direction.
   */
  @action.bound
  setSortDirection(direction?: Direction) {
    if (!direction) {
      this.setSortField(undefined)
      return
    }

    this.sorting = {
      field: this.sorting.field,
      direction,
    }
  }

  /**
   * Clears the filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Removes the documents with the given IDs from the scroller.
   */
  @action.bound
  removeDocuments(ids: string[]) {
    this.scroller.remove((document) => ids.includes(Id.assert(document.id)))
    this.totalDocuments -= ids.length
  }

  /**
   * Fetch documents.
   *
   * @param params
   */
  private async fetchDocuments(
    params: SearchDocumentsQuery,
  ): Promise<CursorPaged<Document>> {
    try {
      const page = await this.documentStore.searchDocuments({
        ...params,
      })

      const jobIds = uniq(
        page.data
          .map((doc) => doc.links.job.jobIds.map((jobId) => Id.assert(jobId)))
          .flat(),
      )

      this.jobStore.fetchJobs(jobIds)

      return page
    } catch (error) {
      this.newAlert()
        .fromError(error)
        .withActions({
          title: 'Try again',
          handler: () => this.searchTrigger.next(undefined),
        })
        .show()
      console.error(error)
      return CursorPaged.empty
    }
  }

  /**
   * Get available workspaces.
   */
  getWorkspaces() {
    return this.loadFilterItemsAdapter.getWorkspaces()
  }

  /**
   * Fetch searchable jobs.
   */
  private fetchSearchableJobs(
    paging: CursorPageQuery,
    filter?: Record<string, unknown>,
  ) {
    const searchText = filter?.searchText as string | undefined

    return this.loadFilterItemsAdapter.fetchSearchableJobs({
      ...paging,
      ...(!!searchText && { searchText }),
      filter: `members.user_public_id:"${this.userPublicId}"`,
      scope: JobScopeOption.All,
    })
  }

  /**
   * Fetch document types.
   */
  private fetchDocumentTypes() {
    return this.loadFilterItemsAdapter.fetchDocumentTypes()
  }

  /**
   * Subscribes to the pending state of the fetch task.
   * Once it flips to `false`, stop.
   */
  private getIsFirstFetchRunning = fromStream(
    true,
    toStream(() => this.scroller.fetch.pending, true).pipe(
      takeWhile(Boolean, true),
    ),
  )
}

/**
 * Current state of searching.
 */
export const enum SearchState {
  /**
   * The initial search is currently running.
   */
  InitialSearchRunning,
  /**
   * The search returned something.
   */
  Some,
  /**
   * The search returned an empty result, but
   * there was a filter applied.
   */
  EmptyButFilterApplied,
  /**
   * The search returned an empty result
   * and no filters were applied.
   */
  CompletelyEmpty,
}

/**
 * Document search result.
 */
export interface DocumentSearchResult {
  /**
   * The search filter that was used.
   */
  searchFilter: DocumentSearchFilter
  /**
   * Whether any search filters were applied.
   */
  filtersApplied: boolean
  /**
   * The total number of search results (not just what was returned).
   */
  total: number
  /**
   * The current list of documents contained in the searcher.
   */
  readonly documents: readonly Document[]
}

export interface DocumentSort {
  field?: DocumentsSortField
  direction?: Direction
}

/**
 * Document filter.
 */
export type DocumentFilter = {
  workspaces: Id[]
  jobs: Id[]
  documentTypes: Id[]
  archived?: Array<boolean | undefined>
}

/**
 * Document search filter.
 */
export interface DocumentSearchFilter {
  searchText?: string
  workspaceIds?: Id[] | null
  jobIds?: Id[] | null
  documentTypeIds?: Id[] | null
  archived?: boolean
  sort: DocumentSort
}

export const DocumentSearchFilter = {
  /**
   * An empty search filter.
   */
  none: {
    searchText: '',
    workspaceIds: [],
    jobIds: [],
    documentTypeIds: [],
    archived: undefined,
    sort: {
      field: undefined,
      direction: undefined,
    },
  } as DocumentSearchFilter,

  /**
   * Whether the specified search filter is considered empty.
   */
  isNone: (searchFilter: DocumentSearchFilter) =>
    DocumentSearchFilter.equals(DocumentSearchFilter.none, searchFilter),

  /**
   * Whether 2 search filters are considered equal.
   */
  equals: (
    left: Partial<DocumentSearchFilter>,
    right: Partial<DocumentSearchFilter>,
  ) => areDeepEqual(left, right),
}

/**
 * Creates a `SearchDocumentsQuery` from the paging and filter.
 *
 * @param paging
 * @param filter
 */
function toSearchDocumentsQuery(
  userPublicId: Id,
  paging: CursorPageQuery,
  filter: DocumentSearchFilter | undefined,
): SearchDocumentsQuery {
  const query: AdminDocumentsQuery = {
    context: 'Admin',
    searchText: filter?.searchText || undefined,
    workspaceIds: filter?.workspaceIds?.map((id) => Id.assert(id)),
    ownerPublicIds: [Id.assert(userPublicId)],
    jobIds: filter?.jobIds?.map((id) => Id.assert(id)),
    documentTypeIds: filter?.documentTypeIds?.map((id) => Id.assert(id)),
    archived: filter?.archived,
    limit: paging.limit,
    after: paging.after,
  }

  if (filter?.sort.field && filter?.sort.direction) {
    query.orderBy = {
      field: filter.sort.field,
      direction: filter.sort.direction,
    }
  }

  return query
}
